/* @flow weak */
import LeftRight from 'components/LeftRight.jsx';
import React from 'react';
import shallowCompare from 'react-addons-shallow-compare';

import cx from 'classnames';

import styles from './styles/FlexibleBlock.css';

/**
 * Abstract layout element used to add left and right aligned content in fluid-width containers.
 *
 * `FlexibleBlock` is commonly referred to as ["Media Object"](http://www.stubbornella.org/content/2010/06/25/the-media-object-saves-hundreds-of-lines-of-code/).
 * It is an extremely common layout that can be continuously nested. It is not as powerful as
 * flexbox, but it is fully-supported by every browser (old and new).
 *
 * ```example
 *  <div>
 *    <FlexibleBlock flex="right" style={{marginBottom: 20}}>
 *      <Glyph name="mobile" color="violet" size="xxxlarge" style={{marginRight: 10}} />
 *      <div>
 *        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras nunc lorem, ornare eu elit
 *        eget, ultrices convallis odio. Maecenas convallis finibus ante. Donec quis turpis tellus.
 *        Sed id ligula molestie dolor vulputate efficitur ac a enim. Aliquam sit amet convallis
 *        urna, et viverra turpis.
 *      </div>
 *    </FlexibleBlock>
 *    <FlexibleBlock flex="left">
 *      <div>
 *        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras nunc lorem, ornare eu elit
 *        eget, ultrices convallis odio. Maecenas convallis finibus ante. Donec quis turpis tellus.
 *        Sed id ligula molestie dolor vulputate efficitur ac a enim. Aliquam sit amet convallis
 *        urna, et viverra turpis.
 *      </div>
 *      <Glyph name="gear" color="jungle" size="xxxlarge" style={{marginLeft: 10}} />
 *    </FlexibleBlock>
 *  </div>
 * ```
 *
 * # Text truncation
 *
 * One major benefit of `FlexibleBlock` (over a `table`, for example) is that it is capable of
 * truncating text without defining any fixed widths.
 *
 * ```example
 *  <FlexibleBlock flex="left">
 *    <Text truncate={true}>
 *      Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras nunc lorem, ornare eu elit
 *      eget, ultrices convallis odio. Maecenas convallis finibus ante. Donec quis turpis tellus.
 *    </Text>
 *    <Glyph name="pencil" color="brand" size="large" style={{marginLeft: 10}} />
 *  </FlexibleBlock>
 *
 * ```
 *
 * # Caveat: Working with absolute-positioned elements
 *
 * `FlexibleBlock` is not well-suited with absolute-positioned elements (e.g., `react-select`)
 * because it built to work with [Layer](/components/Layer). It uses `overflow: hidden`, which will
 * clip any absolutely positioned element that is positioned outside of the flexible region.
 *
 * The `allowOverflow` prop is a hack that works around this problem (with a few caveats with text
 * truncation). Use `allowOverflow` if you absolutely need to. Otherwise, using flexbox may be a
 * better long-term solution.
 *
 * ```example
 *  <div>
 *    <FlexibleBlock flex="right" style={{marginBottom: 20}}>
 *      <Glyph name="crown" color="sunrise" size="xxxlarge" style={{marginRight: 10}} />
 *      <div style={{paddingBottom: 30}}>
 *        <div style={{position: 'relative'}}>
 *          This text has an element below it that is clipped.
 *          <div style={{position: 'absolute', top: '100%'}}>
 *            <div style={{padding: 20, border: '1px solid #000'}}>
 *              This is an absolute positioned element.
 *            </div>
 *          </div>
 *        </div>
 *      </div>
 *    </FlexibleBlock>
 *    <FlexibleBlock flex="right" allowOverflow={true} style={{marginBottom: 30}}>
 *      <Glyph name="crown" color="sunrise" size="xxxlarge" style={{marginRight: 10}} />
 *      <div style={{paddingBottom: 30}}>
 *        <div style={{position: 'relative'}}>
 *          This text has an element below it that is not clipped.
 *          <div style={{position: 'absolute', top: '100%'}}>
 *            <div style={{padding: 20, border: '1px solid #000'}}>
 *              This is an absolute positioned element.
 *            </div>
 *          </div>
 *        </div>
 *      </div>
 *    </FlexibleBlock>

 *  </div>
 *
 * ```
 */

class FlexibleBlock extends React.Component {
  static propTypes = {
    ...LeftRight.propTypes,
    flex: React.PropTypes.oneOf(['left', 'right']).isRequired,
    children(props, propName) {
      if (React.Children.count(props[propName]) !== 2) {
        return new Error('FlexibleBlock expects two child elements');
      }
    },
    allowOverflow: React.PropTypes.bool,
  };

  static defaultProps = {
    allowOverflow: false,
  };

  static wrap(node) {
    if (React.isValidElement(node)) {
      return node;
    }
    return <div>{node}</div>;
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  render() {
    let [left, right] = this.props.children;

    if (this.props.flex === 'left') {
      left = FlexibleBlock.wrap(left);
      left = React.cloneElement(left, {
        className: cx(
          left.props.className,
          this.props.allowOverflow ? styles.flexOverflow : styles.flex
        ),
      });
    } else {
      right = FlexibleBlock.wrap(right);
      right = React.cloneElement(right, {
        className: cx(
          right.props.className,
          this.props.allowOverflow ? styles.flexOverflow : styles.flex
        ),
      });
    }

    return (
      <LeftRight {...this.props} pull={this.props.flex === 'left' ? 'right' : 'left'}>
        {left}
        {right}
      </LeftRight>
    );
  }
}

export default FlexibleBlock;



// WEBPACK FOOTER //
// ./src/components/FlexibleBlock.jsx